"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  number: "01",
  name: "Website Design",
  tagline: "high-conversion digital storefronts",
  description: "We build custom, responsive websites tailored to your brand identity. Our design process focuses on user experience, fast loading speeds, and clear calls-to-action to turn your visitors into loyal customers.",
  price: "FROM $599",
  duration: "/\xA0 \xA02 WEEKS"
}, {
  number: "02",
  name: "SEO Strategy",
  tagline: "dominate search rankings",
  description: "Improve your organic visibility with data-driven SEO strategies. We conduct deep keyword research, technical audits, and content optimization to ensure your business appears when your customers search for you.",
  price: "FROM $299",
  duration: "MONTHLY"
}, {
  number: "03",
  name: "Local SEO",
  tagline: "solve google listing issues",
  description: "Is your business missing from Google Maps? We specialize in fixing Google Business Profile suspensions, optimizing local citations, and managing reviews to help you capture your local market.",
  price: "FROM $199",
  duration: "ONGOING"
}];
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5052" id="services" className="bg-background text-foreground pt-16 pb-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="col-span-12 flex items-center justify-between mb-16">
            <nav aria-label="breadcrumb" className="font-default text-xs tracking-[0.2em] text-muted-foreground">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/HomePage" className="hover:text-primary transition-colors duration-300">
                    ASAHI
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground">SERVICES</li>
              </ol>
            </nav>
            <span className="font-default text-xs tracking-[0.2em] text-primary">
              PRECISION GROWTH
            </span>
          </div>

          <h2 className="col-span-12 font-default font-bold leading-[1.05] tracking-tight !text-4xl lg:col-span-9">Digital Solutions for Your&nbsp;<em className="italic text-primary" data-ast-id="f:/src/pages/Home/sections/Services.tsx|sig:jlcswt" data-ast-start="2761" data-ast-end="2807" contenteditable="false" style={{}}>Growing</em> <em className="italic text-primary" data-ast-id="f:/src/pages/Home/sections/Services.tsx|sig:jlcswu" data-ast-start="2830" data-ast-end="2879">Business</em></h2>

          
        </div>

        <div className="border-t border-border">
          {services.map((service, i) => <ServiceRow key={service.number} service={service} i={i} />)}
        </div>

        <div className="mt-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3 text-center">
            <p className="font-default text-xs tracking-[0.2em] text-muted-foreground mb-6">
              CUSTOM INQUIRIES
            </p>
            <p className="font-default text-2xl md:text-3xl font-bold leading-relaxed mb-10">
              Need a tailored strategy for your specific industry? Let's discuss how we can help you reach your growth goals.
            </p>
            <Button asChild variant="outline" size="lg" className="font-default text-xs tracking-[0.2em] border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary px-8 h-12 rounded-lg">
              <Link to="/ContactUsPage">
                REQUEST CONSULTATION
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
type Service = {
  number: string;
  name: string;
  tagline: string;
  description: string;
  price: string;
  duration: string;
};
function ServiceRow({
  service,
  i
}: {
  service: Service;
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-index={i} ref={ref} className={`group relative border-b border-border transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      <div className="absolute inset-0 bg-accent/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative grid grid-cols-12 gap-4 md:gap-6 py-12 md:py-16 transition-colors duration-500 group-hover:text-primary">
        <div className="col-span-3 md:col-span-1">
          <span className="font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-none text-primary block">
            {service.number}
          </span>
        </div>

        <div className="col-span-9 md:col-span-4 flex flex-col justify-start md:pl-4">
          <h3 className="font-default font-bold text-2xl md:text-3xl lg:text-4xl leading-tight mb-2 text-foreground transition-colors duration-500 group-hover:text-primary">
            {service.name}
          </h3>
          <p className="font-default italic text-base md:text-lg text-muted-foreground">
            {service.tagline}
          </p>
        </div>

        <div className="col-span-12 md:col-span-5 flex items-start">
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-default">
            {service.description}
          </p>
        </div>

        <div className="col-span-12 md:col-span-2 flex flex-row md:flex-col items-start md:items-end justify-between md:justify-start gap-4 md:gap-3">
          <div className="font-default text-xs tracking-[0.15em] text-foreground md:text-right">
            <div>{service.price}</div>
            <div className="text-muted-foreground mt-1">/ {service.duration}</div>
          </div>
          <Link to="/contact-us/" aria-label={`Inquire about ${service.name}`} className="inline-flex items-center justify-center size-10 text-foreground group-hover:text-primary transition-all duration-500">
            <ArrowRight className="size-5 transition-transform duration-500 group-hover:translate-x-2" />
          </Link>
        </div>
      </div>
    </div>;
}